import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Input } from '../../components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import { 
  Users, 
  Search, 
  Eye, 
  Download, 
  FileText, 
  ShoppingCart, 
  Euro, 
  Calendar, 
  Loader2, 
  Clock, 
  CheckCircle, 
  XCircle,
  AlertTriangle,
  Building2,
  User,
  MapPin,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const saleStatusConfig = {
  devis_signe: { label: 'Devis signe', class: 'bg-blue-100 text-blue-700' },
  facture_envoyee: { label: 'Facture envoyee', class: 'bg-purple-100 text-purple-700' },
  paiement_recu: { label: 'Paiement recu', class: 'bg-teal-100 text-teal-700' },
  securisation: { label: 'En securisation', class: 'bg-amber-100 text-amber-700' },
  commission_eligible: { label: 'Commission eligible', class: 'bg-emerald-100 text-emerald-700' },
  paye: { label: 'Paye', class: 'bg-green-100 text-green-800' },
  rejete: { label: 'Rejete', class: 'bg-red-100 text-red-700' },
};

const fdcStatusConfig = {
  pending: { label: 'En cours de validation', class: 'bg-amber-100 text-amber-700', icon: Clock },
  validated: { label: 'Valide', class: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  rejected: { label: 'Refuse', class: 'bg-red-100 text-red-700', icon: XCircle },
};

export const RHCommercials = () => {
  const [commercials, setCommercials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCommercial, setSelectedCommercial] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [commercialSales, setCommercialSales] = useState([]);
  const [commercialDocs, setCommercialDocs] = useState([]);
  const [fdcDocuments, setFdcDocuments] = useState([]);
  const [downloading, setDownloading] = useState(null);
  const [downloadingFdcFile, setDownloadingFdcFile] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchCommercials();
  }, []);

  const fetchCommercials = async () => {
    try {
      const response = await fetch(`${API}/rh/commercials`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setCommercials(data);
      }
    } catch (error) {
      console.error('Failed to fetch commercials:', error);
    } finally {
      setLoading(false);
    }
  };

  const openCommercialDetail = async (commercial) => {
    setSelectedCommercial(commercial);
    setIsDetailOpen(true);
    setLoadingDetail(true);
    setCommercialSales([]);
    setCommercialDocs([]);
    setFdcDocuments([]);
    
    // Fetch FDC documents (works for both pending and validated)
    const fdcId = commercial.fdc_id || commercial.fdc_info?.fdc_id;
    if (fdcId) {
      try {
        const fdcDocsRes = await fetch(`${API}/fdc/${fdcId}/documents`, { credentials: 'include' });
        if (fdcDocsRes.ok) setFdcDocuments(await fdcDocsRes.json());
      } catch (error) {
        console.error('Failed to fetch FDC documents:', error);
      }
    }
    
    // Only fetch sales/docs for validated commercials (real users)
    if (!commercial.is_fdc) {
      try {
        const [salesRes, docsRes] = await Promise.all([
          fetch(`${API}/rh/commercial/${commercial.user_id}/sales`, { credentials: 'include' }),
          fetch(`${API}/rh/commercial/${commercial.user_id}/documents`, { credentials: 'include' }),
        ]);
        
        if (salesRes.ok) setCommercialSales(await salesRes.json());
        if (docsRes.ok) setCommercialDocs(await docsRes.json());
      } catch (error) {
        console.error('Failed to fetch commercial details:', error);
      }
    }
    setLoadingDetail(false);
  };

  const downloadFdcFile = async (fileId, filename, docType) => {
    const fdcId = selectedCommercial?.fdc_id || selectedCommercial?.fdc_info?.fdc_id;
    if (!fdcId) return;
    setDownloadingFdcFile(fileId);
    try {
      const response = await fetch(
        `${API}/fdc/${fdcId}/documents/${fileId}/download`,
        { credentials: 'include' }
      );
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename || `${docType}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success('Document telecharge');
      } else {
        toast.error('Erreur de telechargement');
      }
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Erreur de telechargement');
    } finally {
      setDownloadingFdcFile(null);
    }
  };

  const downloadDocument = async (docId, filename) => {
    setDownloading(docId);
    try {
      const response = await fetch(`${API}/rh/commercial-documents/${docId}/download`, {
        credentials: 'include',
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success('Document telecharge');
      } else {
        toast.error('Erreur lors du telechargement');
      }
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Erreur lors du telechargement');
    } finally {
      setDownloading(null);
    }
  };

  const filteredCommercials = commercials.filter(c =>
    c.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.company_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Separate by status
  const pendingCommercials = filteredCommercials.filter(c => c.fdc_status === 'pending');
  const rejectedCommercials = filteredCommercials.filter(c => c.fdc_status === 'rejected');
  const validatedCommercials = filteredCommercials.filter(c => c.fdc_status === 'validated' || !c.is_fdc);

  const totalCA = commercials.filter(c => !c.is_fdc).reduce((sum, c) => sum + (c.total_sales || 0), 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900 font-[Manrope]" data-testid="rh-commercials-title">
          Mes commerciaux
        </h1>
        <p className="text-slate-500 mt-1">
          Suivi des agents commerciaux rattaches a votre portefeuille
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-indigo-100">
              <Users className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Commerciaux actifs</p>
              <p className="text-xl font-bold text-slate-900">{validatedCommercials.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-amber-100">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">En validation</p>
              <p className="text-xl font-bold text-amber-600">{pendingCommercials.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-red-100">
              <XCircle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Refuses</p>
              <p className="text-xl font-bold text-red-600">{rejectedCommercials.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-emerald-100">
              <Euro className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">CA Total genere</p>
              <p className="text-xl font-bold text-emerald-600">{totalCA.toLocaleString('fr-FR')} €</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          placeholder="Rechercher un commercial..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
          data-testid="search-commercials"
        />
      </div>

      {/* Rejected FDCs with alert */}
      {rejectedCommercials.length > 0 && (
        <Card className="border-0 shadow-sm border-l-4 border-l-red-500 bg-red-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5" />
              Declarations refusees ({rejectedCommercials.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-red-600 mb-4">
              Ces declarations ont ete refusees. Cliquez sur "Voir details" pour voir le motif et corriger.
            </p>
            <div className="space-y-3">
              {rejectedCommercials.map((commercial) => (
                <div key={commercial.user_id} className="flex items-center justify-between p-4 bg-white rounded-lg border border-red-200">
                  <div className="flex items-center gap-3">
                    <Avatar className="w-10 h-10">
                      <AvatarFallback className="bg-red-100 text-red-600">
                        {commercial.name?.charAt(0)}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-medium">{commercial.name}</p>
                      <p className="text-sm text-slate-500">{commercial.company_name}</p>
                      {commercial.rejection_comment && (
                        <p className="text-sm text-red-600 mt-1">
                          <span className="font-medium">Motif:</span> {commercial.rejection_comment}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openCommercialDetail(commercial)}
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      Voir details
                    </Button>
                    <Button
                      size="sm"
                      className="bg-indigo-600 hover:bg-indigo-700"
                      onClick={() => navigate('/rh/fdc')}
                    >
                      <RefreshCw className="w-4 h-4 mr-1" />
                      Corriger
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Pending FDCs */}
      {pendingCommercials.length > 0 && (
        <Card className="border-0 shadow-sm border-l-4 border-l-amber-500 bg-amber-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2 text-amber-700">
              <Clock className="w-5 h-5" />
              En cours de validation ({pendingCommercials.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-amber-600 mb-4">
              Ces declarations sont en attente de validation par l'administration.
            </p>
            <div className="space-y-3">
              {pendingCommercials.map((commercial) => (
                <div key={commercial.user_id} className="flex items-center justify-between p-4 bg-white rounded-lg border border-amber-200">
                  <div className="flex items-center gap-3">
                    <Avatar className="w-10 h-10">
                      <AvatarFallback className="bg-amber-100 text-amber-600">
                        {commercial.name?.charAt(0)}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-medium">{commercial.name}</p>
                      <p className="text-sm text-slate-500">{commercial.company_name}</p>
                      <p className="text-xs text-amber-600">
                        Soumis le {new Date(commercial.created_at).toLocaleDateString('fr-FR')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-amber-100 text-amber-700 border border-amber-300">
                      <Clock className="w-3 h-3 mr-1" />
                      En attente
                    </Badge>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openCommercialDetail(commercial)}
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      Voir details
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Validated Commercials Table */}
      <Card className="border-0 shadow-sm" data-testid="commercials-table">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-emerald-600" />
            Commerciaux actifs ({validatedCommercials.length})
          </CardTitle>
        </CardHeader>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Commercial</TableHead>
                <TableHead>Societe</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>CA Total</TableHead>
                <TableHead>Ventes</TableHead>
                <TableHead>Fin DEC</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                    Chargement...
                  </TableCell>
                </TableRow>
              ) : validatedCommercials.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                    Aucun commercial actif
                  </TableCell>
                </TableRow>
              ) : (
                validatedCommercials.map((commercial) => (
                  <TableRow key={commercial.user_id} className="hover:bg-slate-50">
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar className="w-10 h-10">
                          <AvatarImage src={commercial.picture} />
                          <AvatarFallback className="bg-indigo-100 text-indigo-600">
                            {commercial.name?.charAt(0)}
                          </AvatarFallback>
                        </Avatar>
                        <span className="font-medium">{commercial.name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-600">
                      {commercial.fdc_info?.company_name || '-'}
                    </TableCell>
                    <TableCell className="text-slate-500">{commercial.email}</TableCell>
                    <TableCell className="font-semibold text-emerald-600">
                      {(commercial.total_sales || 0).toLocaleString('fr-FR')} €
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{commercial.sales_count || 0}</Badge>
                    </TableCell>
                    <TableCell>
                      {commercial.dec_end_date ? (
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-slate-400" />
                          <span className="text-sm">{new Date(commercial.dec_end_date).toLocaleDateString('fr-FR')}</span>
                        </div>
                      ) : '-'}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openCommercialDetail(commercial)}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        Voir details
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Commercial Detail Dialog */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <Avatar className="w-10 h-10">
                <AvatarFallback className={`${
                  selectedCommercial?.fdc_status === 'pending' ? 'bg-amber-100 text-amber-600' :
                  selectedCommercial?.fdc_status === 'rejected' ? 'bg-red-100 text-red-600' :
                  'bg-indigo-100 text-indigo-600'
                }`}>
                  {selectedCommercial?.name?.charAt(0)}
                </AvatarFallback>
              </Avatar>
              <div>
                {selectedCommercial?.name}
                <Badge className={`ml-2 ${fdcStatusConfig[selectedCommercial?.fdc_status]?.class || 'bg-slate-100'}`}>
                  {fdcStatusConfig[selectedCommercial?.fdc_status]?.label || 'Actif'}
                </Badge>
              </div>
            </DialogTitle>
            <DialogDescription>
              {selectedCommercial?.email}
            </DialogDescription>
          </DialogHeader>

          {/* Rejection Alert */}
          {selectedCommercial?.fdc_status === 'rejected' && selectedCommercial?.rejection_comment && (
            <div className="p-4 bg-red-50 rounded-lg border border-red-200 mt-4">
              <div className="flex items-start gap-3">
                <XCircle className="w-5 h-5 text-red-600 mt-0.5" />
                <div>
                  <p className="font-semibold text-red-800">Motif du refus</p>
                  <p className="text-red-700">{selectedCommercial.rejection_comment}</p>
                </div>
              </div>
              <Button
                className="mt-3 bg-indigo-600 hover:bg-indigo-700"
                size="sm"
                onClick={() => {
                  setIsDetailOpen(false);
                  navigate('/rh/fdc');
                }}
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Corriger la declaration
              </Button>
            </div>
          )}

          {loadingDetail ? (
            <div className="py-12 text-center">
              <Loader2 className="w-8 h-8 animate-spin mx-auto text-indigo-600" />
            </div>
          ) : (
            <Tabs defaultValue="info" className="mt-4">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="info">Informations</TabsTrigger>
                <TabsTrigger value="sales" disabled={selectedCommercial?.is_fdc}>
                  Ventes ({commercialSales.length})
                </TabsTrigger>
                <TabsTrigger value="documents" disabled={selectedCommercial?.is_fdc}>
                  Documents ({commercialDocs.length})
                </TabsTrigger>
              </TabsList>

              {/* Info Tab - Always available */}
              <TabsContent value="info" className="mt-4 space-y-6">
                {/* Company Info */}
                <div className="space-y-3">
                  <h3 className="font-semibold flex items-center gap-2 text-indigo-600">
                    <Building2 className="w-4 h-4" />
                    Informations de la societe
                  </h3>
                  <div className="grid grid-cols-2 gap-4 bg-slate-50 p-4 rounded-lg">
                    <div>
                      <p className="text-sm text-slate-500">Nom de la societe</p>
                      <p className="font-medium">
                        {selectedCommercial?.company_name || selectedCommercial?.fdc_info?.company_name || '-'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Forme sociale</p>
                      <p className="font-medium">
                        {selectedCommercial?.company_legal_form || selectedCommercial?.fdc_info?.company_legal_form || '-'}
                      </p>
                    </div>
                    <div className="col-span-2">
                      <p className="text-sm text-slate-500">Adresse complete</p>
                      <p className="font-medium">
                        {selectedCommercial?.company_address || selectedCommercial?.fdc_info?.company_address || '-'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">SIREN</p>
                      <p className="font-medium">
                        {selectedCommercial?.company_siren || selectedCommercial?.fdc_info?.company_siren || '-'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Numero RSAC</p>
                      <p className="font-medium">
                        {selectedCommercial?.company_rsac_number || selectedCommercial?.fdc_info?.company_rsac_number || '-'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Ville RSAC</p>
                      <p className="font-medium">
                        {selectedCommercial?.company_rsac_city || selectedCommercial?.fdc_info?.company_rsac_city || '-'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Person Info */}
                <div className="space-y-3">
                  <h3 className="font-semibold flex items-center gap-2 text-indigo-600">
                    <User className="w-4 h-4" />
                    Representant legal
                  </h3>
                  <div className="grid grid-cols-2 gap-4 bg-slate-50 p-4 rounded-lg">
                    <div>
                      <p className="text-sm text-slate-500">Nom Prenom</p>
                      <p className="font-medium">
                        {selectedCommercial?.person_name || selectedCommercial?.fdc_info?.person_name || '-'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Fonction</p>
                      <p className="font-medium">
                        {selectedCommercial?.person_function || selectedCommercial?.fdc_info?.person_function || '-'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Commercial Info */}
                <div className="space-y-3">
                  <h3 className="font-semibold flex items-center gap-2 text-indigo-600">
                    <MapPin className="w-4 h-4" />
                    Informations du commercial
                  </h3>
                  <div className="grid grid-cols-2 gap-4 bg-slate-50 p-4 rounded-lg">
                    <div>
                      <p className="text-sm text-slate-500">Nom complet</p>
                      <p className="font-medium">{selectedCommercial?.name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Email</p>
                      <p className="font-medium">{selectedCommercial?.email}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Telephone</p>
                      <p className="font-medium">
                        {selectedCommercial?.phone || selectedCommercial?.fdc_info?.commercial_phone || '-'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Type de contrat</p>
                      <p className="font-medium">
                        {selectedCommercial?.contract_type || selectedCommercial?.fdc_info?.contract_type || '-'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Date signature</p>
                      <p className="font-medium">
                        {(selectedCommercial?.contract_date || selectedCommercial?.fdc_info?.contract_date) 
                          ? new Date(selectedCommercial?.contract_date || selectedCommercial?.fdc_info?.contract_date).toLocaleDateString('fr-FR')
                          : '-'}
                      </p>
                    </div>
                    {selectedCommercial?.dec_end_date && (
                      <div>
                        <p className="text-sm text-slate-500">Fin DEC</p>
                        <p className="font-medium">
                          {new Date(selectedCommercial.dec_end_date).toLocaleDateString('fr-FR')}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Documents FDC */}
                {(selectedCommercial?.fdc_id || selectedCommercial?.fdc_info?.fdc_id) && (
                  <div className="space-y-3">
                    <h3 className="font-semibold flex items-center gap-2 text-indigo-600">
                      <FileText className="w-4 h-4" />
                      Documents du contrat ({fdcDocuments.length})
                    </h3>
                    <div className="bg-slate-50 p-4 rounded-lg space-y-3">
                      {fdcDocuments.length === 0 ? (
                        <p className="text-sm text-slate-500 text-center">Aucun document</p>
                      ) : (
                        fdcDocuments.map((doc) => {
                          const docLabels = {
                            contract: 'Contrat signe',
                            dsh_clf: 'DSH - CLF signe',
                            autres_avenants: 'Autres & Avenants',
                          };
                          const fileId = doc.file_id;
                          const fileName = doc.file_name || `${doc.doc_type}.pdf`;
                          return (
                            <div key={fileId} className="flex items-center justify-between p-3 bg-white rounded-lg border">
                              <div className="flex items-center gap-3">
                                <FileText className="w-5 h-5 text-indigo-500" />
                                <div>
                                  <p className="font-medium">{docLabels[doc.doc_type] || doc.doc_type}</p>
                                  <p className="text-sm text-slate-500">{fileName}</p>
                                </div>
                              </div>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => downloadFdcFile(fileId, fileName, doc.doc_type)}
                                disabled={downloadingFdcFile === fileId}
                                data-testid={`download-rh-fdc-doc-${doc.doc_type}`}
                              >
                                {downloadingFdcFile === fileId ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <>
                                    <Download className="w-4 h-4 mr-1" />
                                    Telecharger
                                  </>
                                )}
                              </Button>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="sales" className="mt-4">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Client</TableHead>
                      <TableHead>Offre</TableHead>
                      <TableHead>Montant</TableHead>
                      <TableHead>Statut</TableHead>
                      <TableHead>Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {commercialSales.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-4 text-slate-500">
                          Aucune vente
                        </TableCell>
                      </TableRow>
                    ) : (
                      commercialSales.map((sale) => (
                        <TableRow key={sale.sale_id}>
                          <TableCell className="font-medium">{sale.client_name}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{sale.offer}</Badge>
                          </TableCell>
                          <TableCell className="font-semibold">{sale.amount?.toLocaleString('fr-FR')} €</TableCell>
                          <TableCell>
                            <Badge className={`${saleStatusConfig[sale.status]?.class || 'bg-slate-100'} border`}>
                              {saleStatusConfig[sale.status]?.label || sale.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-slate-500">
                            {new Date(sale.created_at).toLocaleDateString('fr-FR')}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TabsContent>

              <TabsContent value="documents" className="mt-4">
                <div className="space-y-3">
                  {commercialDocs.length === 0 ? (
                    <p className="text-center text-slate-500 py-8">Aucun document</p>
                  ) : (
                    commercialDocs.map((doc) => (
                      <div key={doc.doc_id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <FileText className="w-5 h-5 text-slate-500" />
                          <div>
                            <p className="font-medium">{doc.filename}</p>
                            <p className="text-xs text-slate-500">
                              {doc.category} - {new Date(doc.uploaded_at).toLocaleDateString('fr-FR')}
                            </p>
                          </div>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => downloadDocument(doc.doc_id, doc.filename)}
                          disabled={downloading === doc.doc_id}
                        >
                          {downloading === doc.doc_id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <Download className="w-4 h-4 mr-1" />
                              Telecharger
                            </>
                          )}
                        </Button>
                      </div>
                    ))
                  )}
                </div>
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
